import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskedLines } from "./motion";

export default function Hero({ profile }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const photo = profile?.photo;

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[100svh] w-full overflow-hidden">
      {/* giant background wordmark */}
      <motion.div
        style={{ y: textY }}
        className="pointer-events-none absolute -bottom-[4vw] left-0 right-0 z-0 select-none"
      >
        <div className="font-serif leading-[0.8] text-[#0f0f0f] text-[24vw] tracking-tighter whitespace-nowrap text-center">
          SUNTHANKAR
        </div>
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-10 pt-32 md:pt-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          {/* left: text */}
          <motion.div style={{ y: textY }} className="lg:col-span-7 order-2 lg:order-1">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="h-px w-10 bg-[#3a3a3a]" />
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-[#a3a3a3]">
                {profile?.location || "Pune, India"}
              </span>
            </motion.div>

            <h1 className="font-serif text-[#FAFAFA] text-[13vw] sm:text-7xl lg:text-8xl xl:text-9xl leading-[0.82] tracking-tighter">
              <MaskedLines lines={["Soham"]} start={0.25} />
              <MaskedLines lines={["Sunthankar"]} start={0.4} lineClassName="italic font-light text-[#c9c9c9]" />
            </h1>

            <div className="mt-8 max-w-xl overflow-hidden">
              <motion.p
                initial={{ y: 30, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.75, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="text-[#b4b4b4] text-lg leading-relaxed"
              >
                Writer, musician and builder working where{" "}
                <span className="text-[#FAFAFA]">technology</span> meets{" "}
                <span className="text-[#FAFAFA]">finance</span>. Here live my stories, my songs,
                and the way I see the world.
              </motion.p>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-read-btn"
                onClick={() => document.querySelector("#writing")?.scrollIntoView({ behavior: "smooth" })}
                className="group inline-flex items-center gap-2 bg-[#FAFAFA] text-[#050505] px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] hover:bg-[#050505] hover:text-[#FAFAFA] border border-[#FAFAFA] transition-colors duration-300"
              >
                Read the writing
                <ArrowDownRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
              </button>
              <button
                data-testid="hero-listen-btn"
                onClick={() => document.querySelector("#music")?.scrollIntoView({ behavior: "smooth" })}
                className="inline-flex items-center gap-2 px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-[#FAFAFA] border border-[#2f2f2f] hover:border-[#FAFAFA] transition-colors duration-300"
              >
                Listen
              </button>
            </motion.div>
          </motion.div>

          {/* right: photo, clipped frame with spotlight */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <motion.div
              initial={{ opacity: 0, y: 40, clipPath: "inset(12% 12% 12% 12%)" }}
              animate={{ opacity: 1, y: 0, clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.3, ease: [0.16, 1, 0.3, 1], delay: 0.35 }}
              className="relative aspect-[3/4] w-full overflow-hidden bg-[#0d0d0d] border border-[#1c1c1c]"
            >
              <motion.img
                style={{ y: imgY, scale: imgScale }}
                src={photo}
                alt="Soham Sunthankar"
                className="h-full w-full object-cover grayscale contrast-[1.05]"
                data-testid="hero-photo"
              />
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-70" />
              <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% 35%, transparent 40%, rgba(5,5,5,0.55) 100%)" }} />
              <div className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#d0d0d0]">
                Est. Pune / IND
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-[#6a6a6a]"
      >
        Scroll
      </motion.div>
    </section>
  );
}
